// src/pages/Settings.js
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  useTheme,
  Divider
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Language as LanguageIcon,
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

import { useI18n } from '../i18n/i18n';
import { getData, saveData } from '../services/storageService';
import { saveAs } from 'file-saver';

// [NOU] Importar package.json per a la versió
import packageJson from '../../package.json';

const DEFAULT_KEYS = [
  'lainsa_records',
  'maintenance',
  'machines',
  'clients',
  'users',
  'current_shift',
  'app_prefs',
  'equipment',
  'machine_notes',
  'machine_maintenance_logs'
];

function isElectron() {
  try {
    return !!(window && window.process && window.process.versions && window.process.versions.electron) || !!window.electronAPI;
  } catch { return false; }
}

export default function Settings() {
  const { lang, setLang, t } = useI18n(); // Ara 't' s'utilitzarà
  const theme = useTheme();
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const fileRef = useRef(null);
  const envElectron = isElectron();

  // Carregar dades per a la vista prèvia
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = [];
        let totalItems = 0;
        
        for (const key of DEFAULT_KEYS) {
          try {
            const val = await getData(key);
            if (val != null) {
              const count = Array.isArray(val)
                ? val.length
                : (val && typeof val === 'object' ? Object.keys(val).length : 1);
              totalItems += count;
              rows.push({ key, count });
            }
          } catch (error) {
            console.warn(`Error loading data for key ${key}:`, error);
          }
        }
        
        if (alive) {
          setPreview(rows);
          // Afegir total
          if (rows.length > 0) {
            rows.push({ key: 'TOTAL', count: totalItems, isTotal: true });
          }
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleBackup = async () => {
    setBackupLoading(true);
    
    try {
      const payload = {
        meta: {
          exportedAt: new Date().toISOString(),
          app: 'LainsaApp',
          // [MODIFICAT] Versió dinàmica
          version: packageJson.version || '0.0.0',
          lang,
          env: envElectron ? 'electron' : 'web',
          totalRecords: preview.find(item => item.isTotal)?.count || 0
        },
        data: {}
      };

      // Recollir dades
      for (const key of DEFAULT_KEYS) {
        try {
          const val = await getData(key);
          if (val != null) payload.data[key] = val;
        } catch (error) {
          console.warn(`Error backing up key ${key}:`, error);
        }
      }

      const blob = new Blob([JSON.stringify(payload, null, 2)], { 
        type: 'application/json;charset=utf-8' 
      });
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `lainsa-backup-${timestamp}.json`;

      saveAs(blob, filename);
      
      // [MODIFICAT] Notificació i18n
      if (window.showNotification) {
        // Utilitzem una clau genèrica d'èxit, ja que no n'hi ha una d'específica per al backup
        window.showNotification(t('edit_record_success'), 'success');
      }
      
    } catch (err) {
      console.error('Backup error:', err);
      // [MODIFICAT] Notificació i18n
      if (window.showNotification) {
        window.showNotification(t('backup_error_save'), 'error');
      }
    } finally {
      setBackupLoading(false);
    }
  };

  const onPickRestore = () => fileRef.current?.click();

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // Resetejar per a re-selecció
    
    if (!file) return;

    setRestoreLoading(true);

    try {
      const text = await file.text();
      const json = JSON.parse(text);
      
      if (!json || typeof json !== 'object' || !json.data) {
        // [MODIFICAT] Error i18n
        throw new Error(t('import_error'));
      }

      const entries = Object.entries(json.data);
      let restoredCount = 0;
      
      for (const [key, val] of entries) {
        await saveData(key, val);
        restoredCount++;
      }

      // Actualitzar idioma si existeix al backup
      if (json.meta?.lang) {
        setLang(json.meta.lang);
      }

      // [MODIFICAT] Notificació i18n
      if (window.showNotification) {
        window.showNotification(
          `${t('import_success')} (${restoredCount} ${t('items')})`, 
          'success'
        );
      }
      
    } catch (err) {
      console.error('Restore error:', err);
      // [MODIFICAT] Notificació i18n
      if (window.showNotification) {
        window.showNotification(
          err.message || t('import_error'), 
          'error'
        );
      }
    } finally {
      setRestoreLoading(false);
    }
  };

  const getEnvironmentInfo = () => {
    return envElectron ? {
      // [MODIFICAT] i18n
      text: t('environment_electron'),
      color: 'success',
      icon: <CheckCircleIcon />
    } : {
      // [MODIFICAT] i18n
      text: t('environment_web'),
      color: 'info',
      icon: <WarningIcon />
    };
  };

  const environmentInfo = getEnvironmentInfo();

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      {/* Capçalera */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: theme.palette.primary.main,
            mx: 'auto',
            mb: 2
          }}
        >
          <SettingsIcon sx={{ fontSize: 40 }} />
        </Avatar>
        {/* [MODIFICAT] Títols i18n */}
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {t('nav_settings')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('app_subtitle')}
        </Typography>
      </Box>

      <Stack spacing={4}>
        {/* Configuració d'idioma */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon color="primary" />
              {/* [MODIFICAT] i18n */}
              {t('settings_lang_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {/* [MODIFICAT] i18n */}
              {t('settings_lang_note')}
            </Typography>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              {/* [MODIFICAT] i18n */}
              <InputLabel>{t('lang_label')}</InputLabel>
              <Select 
                value={lang} 
                // [MODIFICAT] i18n
                label={t('lang_label')} 
                onChange={(e) => setLang(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {/* [MODIFICAT] i18n */}
                <MenuItem value="es">{t('lang_es')}</MenuItem>
                <MenuItem value="uk">{t('lang_uk')}</MenuItem>
                <MenuItem value="ca">{t('lang_ca')}</MenuItem>
                <MenuItem value="en">{t('lang_en')}</MenuItem>
              </Select>
            </FormControl>
            
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              {/* [MODIFICAT] i18n */}
              {t('settings_lang_note')}
            </Alert>
          </CardContent>
        </Card>

        {/* Còpia de seguretat i Restauració */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BackupIcon color="primary" />
              {/* [MODIFICAT] i18n */}
              {t('backup_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {/* [MODIFICAT] i18n */}
              {t('backup_note')}
            </Typography>

            {/* Informació d'entorn */}
            <Box sx={{ mb: 3 }}>
              <Chip
                icon={environmentInfo.icon}
                label={environmentInfo.text} // Ja és i18n
                color={environmentInfo.color}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            </Box>

            {/* Botons d'acció */}
            <Stack direction="row" spacing={2} sx={{ mb: 4, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                startIcon={backupLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleBackup}
                disabled={backupLoading}
                sx={{ borderRadius: 2 }}
              >
                {/* [MODIFICAT] i18n */}
                {backupLoading ? t('common_loading') : t('backup_create')}
              </Button>
              
              <Button 
                variant="outlined" 
                startIcon={restoreLoading ? <CircularProgress size={20} /> : <UploadIcon />}
                onClick={onPickRestore}
                disabled={restoreLoading}
                sx={{ borderRadius: 2 }}
              >
                {/* [MODIFICAT] i18n */}
                {restoreLoading ? t('common_loading') : t('backup_restore')}
              </Button>
              
              <input
                ref={fileRef}
                type="file"
                accept="application/json"
                style={{ display: 'none' }}
                onChange={handleRestore}
              />
            </Stack>

            <Divider sx={{ my: 3 }} />

            {/* Vista prèvia de dades */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {/* [MODIFICAT] i18n */}
              {t('data_preview')}
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      {/* [MODIFICAT] i18n */}
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('key')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('items')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.map((row, index) => (
                      <TableRow 
                        key={row.key}
                        sx={{ 
                          backgroundColor: row.isTotal ? theme.palette.action.selected : 'transparent',
                          '&:last-child td': { borderBottom: row.isTotal ? 2 : 1 }
                        }}
                      >
                        <TableCell 
                          sx={{ 
                            fontWeight: row.isTotal ? 600 : 'normal',
                            fontStyle: row.isTotal ? 'italic' : 'normal'
                          }}
                        >
                          {/* [MODIFICAT] i18n */}
                          {row.isTotal ? t('labels_total_general', 'TOTAL') : row.key}
                        </TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            fontWeight: row.isTotal ? 600 : 'normal'
                          }}
                        >
                          {row.count}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {preview.length === 0 && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    {/* [MODIFICAT] i18n */}
                    {t('meter_analysis_no_data')}
                  </Alert>
                )}

                <Alert severity="warning" sx={{ borderRadius: 2, mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t('common_other', 'Important')}:
                  </Typography>
                  <Typography variant="body2">
                    {/* [MODIFICAT] i18n (reutilitzant clau) */}
                    {t('backup_note')}
                  </Typography>
                </Alert>
              </>
            )}
          </CardContent>
        </Card>

        {/* Informació del Sistema */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            {/* [MODIFICAT] i18n */}
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              {t('about_subtitle')}
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {/* [MODIFICAT] i18n */}
                <Typography variant="body2" color="text.secondary">{t('about_version')}:</Typography>
                {/* [MODIFICAT] Versió dinàmica */}
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {packageJson.version || 'unknown'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {/* [MODIFICAT] i18n */}
                <Typography variant="body2" color="text.secondary">{t('about_environment')}:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {envElectron ? 'Electron' : 'Web'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {/* [MODIFICAT] i18n */}
                <Typography variant="body2" color="text.secondary">{t('lang_label')}:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {/* [MODIFICAT] i18n */}
                  {lang === 'es' ? t('lang_es') : 
                   lang === 'uk' ? t('lang_uk') : 
                   lang === 'ca' ? t('lang_ca') : t('lang_en')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {/* [MODIFICAT] i18n */}
                <Typography variant="body2" color="text.secondary">{t('archive.total_records', 'Total data')}:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {/* [MODIFICAT] i18n */}
                  {preview.find(item => item.isTotal)?.count || 0} {t('items')}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}